/**
 * Cliente HTTP del admin. Online-first: sin cola de mutaciones offline.
 */
import { clearAuth, getAuth } from "./auth";
import { pesos } from "./format";

// Resolución del backend:
//  1. Si `VITE_API_BASE_URL` está seteado, gana (útil para staging/prod).
//  2. En dev usamos cadena vacía → URLs relativas (`/api/v1/...`). El dev
//     server de Vite tiene un proxy que reenvía `/api` a `http://localhost:8000`
//     (ver vite.config.ts). Con esto evitamos mixed-content cuando entramos
//     al admin vía HTTPS desde el celular en LAN — el browser solo ve
//     same-origin HTTPS y el proxy hace HTTP al backend internamente.
function resolveApiBase(): string {
  const envUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (envUrl) return envUrl;
  return "";
}
const API_BASE = resolveApiBase();

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Texto para mostrarle al operador a partir de un error (auditoría UX-04).
 *
 * Los `ApiError` 4xx traen el mensaje del backend, que ya está escrito para el
 * usuario ("El RUT ya existe en el padrón"). Lo demás — fetch sin red, 5xx,
 * respuestas que no son JSON, errores de JavaScript — no se muestra crudo
 * ("Failed to fetch", "HTTP 502"): se reemplaza por qué pasó y qué hacer. El
 * detalle técnico queda en la consola.
 */
export function mensajeDeError(error: unknown, siNoHayMensaje: string): string {
  const base = /[.!?…]$/.test(siNoHayMensaje.trim())
    ? siNoHayMensaje.trim()
    : `${siNoHayMensaje.trim()}.`;
  if (error instanceof ApiError) {
    if (error.status >= 500 || error.code === "http_error") {
      return `${base} El servidor no respondió bien; intenta de nuevo en un momento.`;
    }
    // Sin cuerpo JSON el mensaje es "HTTP 404": tampoco le sirve al usuario.
    if (!error.message || /^HTTP \d+$/.test(error.message)) return base;
    return error.message;
  }
  if (error instanceof TypeError) {
    // fetch rechaza con TypeError cuando no hay conexión o la cortó la red.
    return `${base} No hay conexión con el servidor; revisa tu internet e intenta de nuevo.`;
  }
  if (error !== undefined && error !== null) console.error(error);
  return base;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** Header `Idempotency-Key`: el backend no re-ejecuta un POST repetido con
   * la misma key (devuelve la respuesta original). Una key por envío de
   * formulario, no por request. */
  idempotencyKey?: string;
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, signal, idempotencyKey } = opts;

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (idempotencyKey) {
    headers["Idempotency-Key"] = idempotencyKey;
  }

  const auth = getAuth();
  if (auth) {
    headers["Authorization"] = `Bearer ${auth.accessToken}`;
  }

  const url = `${API_BASE}${path}`;
  const response = await fetch(url, {
    method,
    headers,
    body: body === undefined ? null : JSON.stringify(body),
    signal: signal ?? null,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload: unknown = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    // Sesión expirada / token inválido → limpiar y mandar al login.
    // Excepción: el propio endpoint de login devuelve 401 cuando las
    // credenciales son malas, ahí queremos que el form muestre el error.
    if (response.status === 401 && !path.startsWith("/api/v1/auth/login")) {
      clearAuth();
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        const next = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        window.location.replace(`/login?next=${next}`);
      }
    }
    if (isJson && typeof payload === "object" && payload !== null && "error" in payload) {
      const err = (payload as {
        error: { code: string; message: string; details?: Record<string, unknown> };
      }).error;
      throw new ApiError(response.status, err.code, err.message, err.details);
    }
    throw new ApiError(response.status, "http_error", `HTTP ${response.status}`);
  }

  return payload as T;
}

/**
 * Descarga un archivo (CSV/PDF/etc.) preservando el nombre que sugiere el
 * server vía `Content-Disposition`. Forza un click en un anchor temporal.
 */
export async function apiDownload(
  path: string,
  fallbackFilename: string,
): Promise<void> {
  const auth = getAuth();
  const headers: Record<string, string> = {};
  if (auth) headers["Authorization"] = `Bearer ${auth.accessToken}`;

  const response = await fetch(`${API_BASE}${path}`, { headers });
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const j = await response.json();
      if (j?.error?.message) message = j.error.message;
    } catch {
      // ignore
    }
    throw new ApiError(response.status, "download_failed", message);
  }

  const blob = await response.blob();
  const cd = response.headers.get("content-disposition") ?? "";
  const m = /filename="?([^"]+)"?/i.exec(cd);
  const filename = m && m[1] ? m[1] : fallbackFilename;

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * POST con body JSON que retorna un `Blob` (PDF/imagen/etc.) en lugar de JSON.
 * Útil para previews que se renderizan vía `URL.createObjectURL` en un iframe.
 */
export async function apiPostBlob(
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<Blob> {
  const auth = getAuth();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (auth) headers["Authorization"] = `Bearer ${auth.accessToken}`;

  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal: signal ?? null,
  });

  if (!response.ok) {
    if (response.status === 401 && !path.startsWith("/api/v1/auth/login")) {
      clearAuth();
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        const next = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        window.location.replace(`/login?next=${next}`);
      }
    }
    let message = `HTTP ${response.status}`;
    try {
      const j = await response.json();
      if (j?.error?.message) message = j.error.message;
    } catch {
      // ignore
    }
    throw new ApiError(response.status, "post_blob_failed", message);
  }

  return response.blob();
}

export interface UploadResponse {
  kind: string;
  bucket: string;
  key: string;
  url: string;
  url_expires_at: string;
  size_bytes: number;
  content_type: string;
}

/**
 * Sube un archivo al endpoint /api/v1/uploads. Devuelve metadata + URL
 * presignada. La storage key (`response.key`) es lo que se persiste en la
 * entidad dueña vía PATCH dedicado.
 */
export async function apiUploadFile(
  kind: string,
  blob: Blob,
  opts?: { filename?: string; contentType?: string },
): Promise<UploadResponse> {
  const auth = getAuth();
  const fd = new FormData();
  const filename = opts?.filename ?? "upload.bin";
  const contentType = opts?.contentType ?? blob.type ?? "application/octet-stream";
  fd.append("file", new File([blob], filename, { type: contentType }));
  fd.append("kind", kind);

  const headers: Record<string, string> = {};
  if (auth) headers["Authorization"] = `Bearer ${auth.accessToken}`;

  const response = await fetch(`${API_BASE}/api/v1/uploads`, {
    method: "POST",
    headers,
    body: fd,
  });

  if (!response.ok) {
    if (response.status === 401) {
      clearAuth();
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        const next = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        window.location.replace(`/login?next=${next}`);
      }
    }
    let message = `HTTP ${response.status}`;
    try {
      const j = await response.json();
      if (j?.error?.message) message = j.error.message;
    } catch {
      // ignore
    }
    throw new ApiError(response.status, "upload_failed", message);
  }
  return (await response.json()) as UploadResponse;
}

/**
 * POST multipart con un archivo en el campo `file` y respuesta JSON. Lo usan
 * los importadores de planillas (vista previa / confirmar va en el `path`).
 */
export async function apiPostArchivo<T>(path: string, archivo: File): Promise<T> {
  const auth = getAuth();
  const fd = new FormData();
  fd.append("file", archivo);
  const headers: Record<string, string> = {};
  if (auth) headers["Authorization"] = `Bearer ${auth.accessToken}`;

  const response = await fetch(`${API_BASE}${path}`, { method: "POST", headers, body: fd });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401) {
      clearAuth();
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.replace(`/login?next=${next}`);
      }
    }
    const err = (payload as { error?: { code: string; message: string } } | null)?.error;
    throw new ApiError(
      response.status,
      err?.code ?? "http_error",
      err?.message ?? `HTTP ${response.status}`,
    );
  }
  return payload as T;
}

/**
 * Monto en pesos. Alias histórico de `pesos` (lib/format.ts, DESIGN.md §4):
 * una sola regla de formato en todo el admin (VIS-14). El código nuevo usa
 * `pesos` directo. Diferencia con el `Intl` currency de antes: el negativo
 * sale `-$1.234` (antes `$-1.234`); los positivos son idénticos.
 */
export function formatCLP(value: number): string {
  return pesos(value);
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Tiempo relativo en español-CL ("hace 5 minutos", "hace 3 días").
 * Usa Intl.RelativeTimeFormat — disponible en todos los browsers
 * modernos. Para "hace menos de 1 minuto" devuelve "hace instantes".
 */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  const diffMs = now.getTime() - date.getTime();
  const seconds = Math.floor(Math.abs(diffMs) / 1000);
  if (seconds < 30) return "hace instantes";
  const sign = diffMs >= 0 ? -1 : 1;

  const rtf = new Intl.RelativeTimeFormat("es-CL", { numeric: "auto" });
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 60) return rtf.format(sign * seconds, "second");
  if (minutes < 60) return rtf.format(sign * minutes, "minute");
  if (hours < 24) return rtf.format(sign * hours, "hour");
  if (days < 7) return rtf.format(sign * days, "day");
  if (weeks < 5) return rtf.format(sign * weeks, "week");
  if (months < 12) return rtf.format(sign * months, "month");
  return rtf.format(sign * years, "year");
}
